import {
  SIGN_IN_LINK_FLOWS,
  type SignInLink,
  type SignInLinkFlow,
} from "../../convex/constants/signInLinkFlows";
import { EMAIL_FORMAT } from "../formats/email";
import { EMAILED_SIGN_IN_FLOW_KEY } from "./emailed-sign-in-marker";

export interface EmailedSignIn {
  flow: SignInLinkFlow;
  email: string;
  code?: string;
}

export const DEFAULT_SIGN_IN_LINK: SignInLink = { returnPath: "/", flow: "modal" };

const EMAIL_KEY = "email";
const CODE_KEY = "code";
const CODE_PATTERN = /^\d{6}$/;
const MAX_RETURN_PATH_LENGTH = 2048;
const PATH_BASE = "https://return-path.invalid";

export function safeReturnPath(path: string): string {
  if (!path.startsWith("/") || path.startsWith("//") || path.length > MAX_RETURN_PATH_LENGTH) {
    return DEFAULT_SIGN_IN_LINK.returnPath;
  }
  try {
    const url = new URL(path, PATH_BASE);
    if (url.origin !== PATH_BASE) return DEFAULT_SIGN_IN_LINK.returnPath;
    return `${url.pathname}${url.search}`;
  } catch {
    return DEFAULT_SIGN_IN_LINK.returnPath;
  }
}

function isSignInLinkFlow(value: string | null): value is SignInLinkFlow {
  return SIGN_IN_LINK_FLOWS.some((flow) => flow === value);
}

export function emailedSignInHref(origin: string, link: SignInLink, signIn: Omit<EmailedSignIn, "flow">): string {
  const fragment = new URLSearchParams({ [EMAILED_SIGN_IN_FLOW_KEY]: link.flow, [EMAIL_KEY]: signIn.email });
  if (signIn.code) fragment.set(CODE_KEY, signIn.code);
  return `${origin}${safeReturnPath(link.returnPath)}#${fragment.toString()}`;
}

export function parseEmailedSignIn(hash: string): EmailedSignIn | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const flow = params.get(EMAILED_SIGN_IN_FLOW_KEY);
  const email = params.get(EMAIL_KEY)?.trim().toLowerCase();
  if (!isSignInLinkFlow(flow) || !email || !EMAIL_FORMAT.validate(email)) return null;
  const code = params.get(CODE_KEY);
  return code && CODE_PATTERN.test(code) ? { flow, email, code } : { flow, email };
}
